import { computed, inject, Injectable, resource, Signal, signal, WritableSignal } from '@angular/core';
import { ShoppingCartSyncService } from 'src/app/services/shoppingCartSync.service';
import { AppService } from './app.service';
import {
    IShoppingCartWrapper,
    ShoppingCartViewMode,
    IShoppingCartDetailItem_FE,
    IShoppingCartFlyoutItem_FE,
    IProductV2Edit,
    ShoppingCartProduct,
    IShoppingCartSnapshot_FE,
    IUserInfos4Cashregister,
    IDirectDebitGroup4Cashregister,
    IShoppingCartPayment_FE
} from '@vierkant-software/types__api';
import { DateTime } from 'luxon';
import { ProductDetailLevel, ProductSyncService } from 'src/app/services/productSync.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ShoppingCartError } from 'src/util/errors/base-error';
import { RegisterSessionStateService } from './register-session-state.service';


//TODO: Define Filter, etc (stack_ID with quantity 0)
//TODO: Save persistent in ClientSettings

export enum ShoppingCartActionType {
    INCREASE_QUANTITY,
    DECREASE_QUANTITY,
    SET_QUANTITY,
    REMOVE_PRODUCT,
}

interface IShoppingCartServiceProductCallbackOptions {
    quantity?: number;
}

interface IShoppingCartPendingAction {
    key: string;
    subject: Subject<number>;
    subscription: Subscription;
    pendingQuantity: number;
    executor: (pendingQuantity: number) => Promise<void>;
}

interface IShoppingCartGoToParams {
    userId?: string;
    cartId?: string;
}

interface IShoppingCartCreateParams {
    ownerId?: string;
    title?: string;
    viewMode: ShoppingCartViewMode;
}

export interface IShoppingCartServiceProduct {
    ID: string;
    isLoading: boolean;
    productName: string;
    productCode: string[];
    stack_ID: number;
    quantity: number;
    bruttoPrice: number;
    nettoPrice: number;
    vatAmount: number;
    vatRate: number;
    totalBruttoPrice: number;
    totalNettoPrice: number;
    totalVatAmount: number;
    cancellationType: 'none' | 'immediateCancellation' | 'lateCancellation';
    addedByDevice: string;
    addedByUser: string;
    at: DateTime;
    action: ((action: ShoppingCartActionType, options?: IShoppingCartServiceProductCallbackOptions) => Promise<void>) | undefined;
}

export interface IShoppingCartServicePaymentData {
    directDebitGroup_ID?: string;
    userBankAccount_ID?: string;
    cashAmountGiven?: number;
}

export interface IShoppingCartServiceCart {
    ID: string;
    title: string;
    productCount: number;
    totalValueProducts: number;
    totalValueVats: Record<number, number>;
    totalValue: number;
    immediateCancellationTil: DateTime | undefined;
    snapshot: IShoppingCartSnapshot_FE;
    bankAccountsForUser?: IUserInfos4Cashregister;
    directDebitAccountInformation?: IDirectDebitGroup4Cashregister[];
    balanceTotal?: number;
    payment?: IShoppingCartPayment_FE;
}

@Injectable({
  providedIn: 'root'
})
export class ShoppingCartService {
    private appService = inject(AppService);
    private router = inject(Router);
    private registerSessionService = inject(RegisterSessionStateService);

    private get shoppingCartSyncService(): ShoppingCartSyncService {
        return this.appService.shoppingCartSyncService;
    }

    private get productSyncService(): ProductSyncService {
        return this.appService.productSyncService;
    }

    private get api() {
        return this.appService.api;
    }

    private _cartId: WritableSignal<string | null> = signal(null);
    private _viewMode: WritableSignal<ShoppingCartViewMode | null> = signal(null);
    private _readOnlyCartId: WritableSignal<string | null> = signal(null);
    private syncedProductResources = new Map<string, ReturnType<ProductSyncService['get']>>();
    private vatRateSignals = new Map<string, WritableSignal<number>>();
    private debouncedActionSubjects = new Map<string, IShoppingCartPendingAction>();
    private _products: Signal<IShoppingCartServiceProduct>[] = [];
    private _readOnlyProducts: Signal<IShoppingCartServiceProduct>[] = [];
    private readonly debounceTimeMs = 500;
    private cartLoadInFlight: Promise<IShoppingCartWrapper | null> | null = null;
    private cartLoadInFlightKey: string | null = null;
    private actionInFlight: string | null = null;
    private cartActionInFlight: Promise<void> | null = null;

    private currentPaymentType: string | null = null;

    /** whether the next payment started redeems the owner's balances; per cart, reset when it changes */
    public readonly useBalances: WritableSignal<boolean> = signal(false);

    public get cartId(): string | null {
        return this._cartId();
    }

    public set cartId(cartId: string | null) {
        if (this._cartId() === cartId) return;

        this._cartId.set(cartId);
        this.useBalances.set(false);
        this._resetCartLoadGate();
        this._cancelPendingActions();
    }

    public get viewMode(): ShoppingCartViewMode | null {
        return this._viewMode();
    }

    public set viewMode(viewMode: ShoppingCartViewMode | null) {
        this._viewMode.set(viewMode);
        this._resetCartLoadGate();
    }

    public get readOnlyCartId(): string | null {
        return this._readOnlyCartId();
    }

    public set readOnlyCartId(cartId: string | null) {
        this._readOnlyCartId.set(cartId);
    }

    private readonly rawCart = resource({
        params: () => ({
            cartId:   this._cartId(),
            viewMode: this._viewMode()
        }),
        loader: async ({ params }) => {
            try {
                if (!params.cartId || params.viewMode == null) {
                    console.log('[ShoppingCartService] rawCart loader: No cartId or viewMode', params);
                    return null;
                }

                console.log('[ShoppingCartService] rawCart loader: Loading cart', params);
                const cart = await this._getOrLoadCart(params.cartId, params.viewMode, true);
                console.log('[ShoppingCartService] rawCart loader: Successfully loaded cart', cart);
                return cart;
            } catch(error) {
                console.error('[ShoppingCartService] rawCart loader: Error loading cart', { params, error });
                error.displayErrorToast?.();
                throw error;
            }
        }
    });

    private readonly _cart = computed(() => {
        const cartData = this.rawCart.value();
        if (!cartData) return null;
        return this._transformCart(cartData, false);
    });

    public get cart(): IShoppingCartServiceCart | null {
        return this._cart();
    }

    public getProduct(index: number): IShoppingCartServiceProduct | null {
        if (index < 0 || index >= this._products.length) return null;
        return this._products[index]();
    }

    public get productCount(): number {
        return this._products.length;
    }

    private readonly rawReadOnlyCart = resource({
        params: () => ({
            cartId: this._readOnlyCartId()
        }),
        loader: async ({ params }) => {
            try {
                if (!params.cartId) {
                    console.log('[ShoppingCartService] rawReadOnlyCart loader: No cartId', params);
                    return null;
                }

                console.log('[ShoppingCartService] rawReadOnlyCart loader: Loading read-only cart', params);
                const cart = await this.api.ShoppingCartV2Worker.getCart(params.cartId, { detailLevel: ShoppingCartViewMode.checkout });
                await this._loadProductResources(cart);
                console.log('[ShoppingCartService] rawReadOnlyCart loader: Successfully loaded cart', cart);
                return cart;
            } catch(error) {
                console.error('[ShoppingCartService] rawReadOnlyCart loader: Error loading cart', { params, error });
                error.displayErrorToast?.();
            }
        }
    });

    private readonly _readOnlyCart = computed(() => {
        const cartData = this.rawReadOnlyCart.value();
        if (!cartData) return null;
        return this._transformCart(cartData, true);
    });

    public get readOnlyCart(): IShoppingCartServiceCart | null {
        return this._readOnlyCart();
    }

    public getReadOnlyProduct(index: number): IShoppingCartServiceProduct | null {
        if (index < 0 || index >= this._readOnlyProducts.length) return null;
        return this._readOnlyProducts[index]();
    }

    public get readOnlyProductCount(): number {
        return this._readOnlyProducts.length;
    }

    constructor() {
        this.shoppingCartSyncService.dataChange$.subscribe((event) => {
            if (event.type !== 'injected' && event.type !== 'loaded' && event.type !== 'evicted') return;

            const currentCartId = this._cartId();
            const readOnlyCartId = this._readOnlyCartId();
            const affectsCart = currentCartId && event.items.some(item => item.ID === currentCartId);
            const affectsReadOnlyCart = readOnlyCartId && event.items.some(item => item.ID === readOnlyCartId);

            // 'evicted' only reaches us with the backend's force flag, so it means the cart is gone for this client
            if (event.type === 'evicted') {
                if (affectsCart) {
                    console.log('[ShoppingCartService] Synced cart evicted, clearing cart context', currentCartId);
                    this._resetCartContext();
                }

                if (affectsReadOnlyCart) {
                    console.log('[ShoppingCartService] Synced read-only cart evicted, clearing', readOnlyCartId);
                    this._resetReadOnlyCart();
                }
                return;
            }

            if (affectsCart) {
                console.log('[ShoppingCartService] Synced cart updated, reloading...', currentCartId);
                this.rawCart.reload();
            }

            if (affectsReadOnlyCart) {
                console.log('[ShoppingCartService] Synced read-only cart updated, reloading...', readOnlyCartId);
                this.rawReadOnlyCart.reload();
            }
        });
    }

    private _resetCartLoadGate(): void {
        this.cartLoadInFlight = null;
        this.cartLoadInFlightKey = null;
    }

    private _takePendingActions(): IShoppingCartPendingAction[] {
        const pending = Array.from(this.debouncedActionSubjects.values());

        for (const { subject, subscription } of pending) {
            // unsubscribe before complete: debounceTime emits its pending value when the source completes
            subscription.unsubscribe();
            subject.complete();
        }
        this.debouncedActionSubjects.clear();

        return pending.filter(action => action.pendingQuantity !== 0);
    }

    private _cancelPendingActions(): void {
        const dropped = this._takePendingActions();
        if (dropped.length > 0)
            console.warn('[ShoppingCartService] Dropped pending cart actions:', dropped.map(action => action.key));
    }

    // Run the debounced clicks now instead of losing them, so the last '+' still counts towards the checkout action.
    private async _flushPendingActions(): Promise<void> {
        // A debounced action may already be running with a stale serial, so let it finish first.
        await this.cartActionInFlight;

        for (const action of this._takePendingActions()) {
            console.log('[ShoppingCartService] Flushing pending cart action', action.pendingQuantity);
            await action.executor(action.pendingQuantity);
        }
    }

    // Throws instead of returning: the callers navigate on success, so a silent return would fake a completed action.
    private _beginAction(action: string): void {
        if (this.actionInFlight) {
            console.warn('[ShoppingCartService] Rejecting', action, '- still running:', this.actionInFlight);
            throw ShoppingCartError.ActionInProgress();
        }

        this.actionInFlight = action;
    }

    private _assertCartContext(action: string): void {
        if (this._cartId()) return;

        console.warn('[ShoppingCartService] No cart in context, aborting', action);
        throw ShoppingCartError.NoCartContext();
    }

    private _immediateCancellationTil(cart: IShoppingCartWrapper): DateTime | undefined {
        return cart.checkout?.immediateCancellationTil ?? cart.flyout?.immediateCancellationTil;
    }

    private _resetReadOnlyCart(): void {
        this._readOnlyProducts = [];
        this._readOnlyCartId.set(null);
        this.rawReadOnlyCart.set(null);
    }

    // A late resolving call must not restore a cart the service has already left.
    private _applyCart(cart: IShoppingCartWrapper): void {
        if (this._cartId() !== cart.shoppingCart_ID) {
            console.warn('[ShoppingCartService] Discarding update for a cart that is no longer in context:', cart.shoppingCart_ID);
            return;
        }

        this.rawCart.set(cart);
    }

    private async _getOrLoadCart(cartId: string, viewMode: ShoppingCartViewMode, forceReload = false): Promise<IShoppingCartWrapper> {
        const key = `${cartId}_${viewMode}`;
        const currentCart = this.rawCart.value();
        if (!forceReload && currentCart && this.cartId === cartId && this.viewMode === viewMode)
            return currentCart;

        if (this.cartLoadInFlightKey === key) {
            const inFlightCart = await this.cartLoadInFlight!;
            if (!inFlightCart)
                throw new Error('[ShoppingCartService] _getOrLoadCart: Cart loading returned null');
            return inFlightCart;
        }

        this.cartLoadInFlightKey = key;
        this.cartLoadInFlight = this.api.ShoppingCartV2Worker.getCart(cartId, { detailLevel: viewMode })
            .then(async cart => {
                await this._loadProductResources(cart);
                return cart;
            })
            .finally(() => {
                if (this.cartLoadInFlightKey === key) {
                    this.cartLoadInFlight = null;
                    this.cartLoadInFlightKey = null;
                }
            });

        const loadedCart = await this.cartLoadInFlight;
        if (!loadedCart)
            throw new Error('[ShoppingCartService] _getOrLoadCart: Cart loading returned null');
        return loadedCart;
    }

    private async _ensureCartLoaded(): Promise<IShoppingCartWrapper> {
        const cart = await this._getOrLoadCart(this._cartId(), this._viewMode());
        this._applyCart(cart);
        return cart;
    }

    private async _loadProductResources(cart: IShoppingCartWrapper): Promise<void> {
        const productIds = new Set<string>();

        if (cart.checkout?.details)
            cart.checkout.details.forEach(item => productIds.add(item.product_rev_ID));
        else if (cart.flyout?.content)
            Object.values(cart.flyout.content).forEach(item => productIds.add(item.product_rev_ID));

        productIds.forEach(productRevId => this._getOrCreateProductResource(productRevId));
    }

    private _getOrCreateProductResource(productRevId: string | undefined): ReturnType<ProductSyncService['get']> | undefined {
        if (!productRevId)
            return undefined;

        const existingResource = this.syncedProductResources.get(productRevId);
        if (existingResource)
            return existingResource;

        const productResource = this.productSyncService.get(productRevId, ProductDetailLevel.full);
        this.syncedProductResources.set(productRevId, productResource);
        console.log('[ShoppingCartService] Loaded product resource for', productRevId);
        return productResource;
    }

    private _loadVatRate(vatId: string): void {
        if (!this.vatRateSignals.has(vatId)) {
            const vatSignal = signal<number | null>(null);
            this.vatRateSignals.set(vatId, vatSignal as WritableSignal<number>);
            console.log('[ShoppingCartService] Loading VAT rate for', vatId);

            this.api.CashflowWorker.getVATRate(vatId)
                .then(vatRate => {
                    (vatSignal as WritableSignal<number | null>).set(vatRate.vat);
                    console.log('[ShoppingCartService] Loaded VAT rate:', vatId, vatRate.vat);
                })
                .catch(err => {
                    console.warn('[ShoppingCartService] Failed to load VAT rate:', vatId, err);
                    (vatSignal as WritableSignal<number | null>).set(0);
                });
        }
    }

    private _debouncedQuantityChange(
        debounceKey: string,
        quantityDelta: number,
        executor: (pendingQuantity: number) => Promise<void>,
        errorMessage: string
    ): void {
        if (!this.debouncedActionSubjects.has(debounceKey)) {
            const subject = new Subject<number>();
            const subscription = subject
                .pipe(debounceTime(this.debounceTimeMs))
                .subscribe((pendingQuantity) => {
                    const actionData = this.debouncedActionSubjects.get(debounceKey);
                    if (!actionData) return;

                    actionData.subscription.unsubscribe();
                    actionData.subject.complete();
                    this.debouncedActionSubjects.delete(debounceKey);

                    const running = executor(pendingQuantity)
                        .catch(error => {
                            console.error(errorMessage, error);
                            error.displayErrorToast?.();
                        })
                        .finally(() => {
                            if (this.cartActionInFlight === running)
                                this.cartActionInFlight = null;
                        });

                    this.cartActionInFlight = running;
                });

            this.debouncedActionSubjects.set(debounceKey, { key: debounceKey, subject, subscription, pendingQuantity: 0, executor });
        }

        const actionData = this.debouncedActionSubjects.get(debounceKey)!;
        const newPendingQuantity = actionData.pendingQuantity + quantityDelta;

        if (newPendingQuantity === 0) {
            actionData.pendingQuantity = 0;
            return;
        }

        actionData.pendingQuantity = newPendingQuantity;
        actionData.subject.next(newPendingQuantity);
    }

    private _createDebouncedActionCallback(
        productRevId: string,
        stack_IDs: number[],
        readOnly: boolean
    ): ((action: ShoppingCartActionType, options?: IShoppingCartServiceProductCallbackOptions) => Promise<void>) | undefined {
        if (readOnly) return undefined;

        return async (action: ShoppingCartActionType, options?: IShoppingCartServiceProductCallbackOptions): Promise<void> => {
            console.log('[ShoppingCartService] Action called:', { action, productRevId, stack_IDs, options });
            const debounceKey = `${productRevId}_${action}`;
            const quantityDelta = action === ShoppingCartActionType.INCREASE_QUANTITY ? 1 : -1;

            this._debouncedQuantityChange(
                debounceKey,
                quantityDelta,
                (pendingQuantity) => this._executeCartAction({ product_rev_ID: productRevId, quantity: pendingQuantity, stack_ID: stack_IDs[0] }),
                '[ShoppingCartService] Failed to execute action:'
            );
        };
    }

    public async createCart(params: IShoppingCartCreateParams): Promise<void> {
        try {
            const cart = await this.appService.api.ShoppingCartV2Worker.createCart({
                title:   params.title,
                ownerId: params.ownerId
            });

            this.viewMode = params.viewMode;
            this.cartId = cart.shoppingCart_ID;
        } catch(error) {
            console.error('Failed to create cart:', error);
            error?.displayErrorToast?.();
            throw error;
        }
    }

    public async addProduct(productData: ShoppingCartProduct): Promise<void> {
        try {
            const productKey = productData.product_rev_ID || productData.product_ID;
            if (!productKey)
                throw new Error('[ShoppingCartService] addProduct: No product_rev_ID or product_ID provided');

            const debounceKey = `${productKey}_addProduct`;

            this._debouncedQuantityChange(
                debounceKey,
                productData.quantity,
                (pendingQuantity) => this._executeCartAction({ ...productData, quantity: pendingQuantity }),
                '[ShoppingCartService] Failed to add product:'
            );
        } catch(error) {
            error?.displayErrorToast?.();
            throw error;
        }
    }

    public async removeProduct(productData: ShoppingCartProduct): Promise<void> {
        try {
            if (!productData.stack_ID)
                throw new Error('[ShoppingCartService] removeProduct: stack_ID is required');

            const debounceKey = `${productData.stack_ID}_removeProduct`;

            this._debouncedQuantityChange(
                debounceKey,
                -productData.quantity,
                (pendingQuantity) => this._executeCartAction({ ...productData, quantity: pendingQuantity }),
                '[ShoppingCartService] Failed to remove product:'
            );
        } catch(error) {
            error?.displayErrorToast?.();
            throw error;
        }
    }

    public async immediateCancel(): Promise<void> {
        this._beginAction('immediateCancel');

        try {
            this._assertCartContext('immediateCancel');
            await this.cartActionInFlight;
            this._cancelPendingActions();

            const loadedCart = await this._ensureCartLoaded();
            const cancellationTil = this._immediateCancellationTil(loadedCart);
            if (!cancellationTil?.isValid || cancellationTil <= DateTime.serverTime())
                throw ShoppingCartError.CancellationExpired();

            const result = await this.api.ShoppingCartV2Worker.immediateCancellation(loadedCart.shoppingCart_ID, loadedCart.serial);

            // immediateCancellation takes no detailLevel and always answers with the flyout view
            const cart = this._viewMode() === ShoppingCartViewMode.flyout
                ? result
                : await this._getOrLoadCart(loadedCart.shoppingCart_ID, this._viewMode(), true);

            await this._loadProductResources(cart);
            this._applyCart(cart);
        } catch(error) {
            console.error('[ShoppingCartService] Failed to cancel immediately:', error);
            error?.displayErrorToast?.();
            throw error;
        } finally {
            this.actionInFlight = null;
        }
    }

    public async putOnTab(): Promise<void> {
        this._beginAction('putOnTab');

        try {
            this._assertCartContext('putOnTab');
            await this._flushPendingActions();

            const loadedCart = await this._ensureCartLoaded();
            const cartId = loadedCart.shoppingCart_ID;
            const serial = loadedCart.serial;

            await this.api.ShoppingCartV2Worker.putOnTab(cartId, serial, { detailLevel: this._viewMode() });
            this._resetCartContext();
        } catch(error) {
            console.error('[ShoppingCartService] Failed to put cart on tab:', error);
            error?.displayErrorToast?.();
            this._reloadSessionIfRefused(error);
            throw error;
        } finally {
            this.actionInFlight = null;
        }
    }

    public async startPayment(paymentType: string): Promise<void> {
        this._beginAction('startPayment');

        try {
            this._assertCartContext('startPayment');
            await this._flushPendingActions();

            let loadedCart = await this._ensureCartLoaded();
            const cartId = loadedCart.shoppingCart_ID;
            const serial = loadedCart.serial;

            const result = await this.api.ShoppingCartV2Worker.startPaymentProcessV2(
                cartId,
                paymentType,
                serial,
                { detailLevel: this._viewMode(), useBalances: this.useBalances() },
                true
            );

            this.currentPaymentType = paymentType;
            this._applyCart(result);
        } catch(error) {
            console.error('[ShoppingCartService] Failed to start payment process:', error);
            error?.displayErrorToast?.();
            this._reloadSessionIfRefused(error);
            throw error;
        } finally {
            this.actionInFlight = null;
        }
    }

    // Opens the payment putOnTab settles, so the dialog can show what the balances cover first.
    public async startPutOnTab(): Promise<void> {
        this._beginAction('startPutOnTab');

        try {
            this._assertCartContext('startPutOnTab');
            await this._flushPendingActions();

            const loadedCart = await this._ensureCartLoaded();
            const cartId = loadedCart.shoppingCart_ID;
            const serial = loadedCart.serial;

            const result = await this.api.ShoppingCartV2Worker.startPutOnTab(
                cartId,
                serial,
                { detailLevel: this._viewMode(), useBalances: this.useBalances() }
            );

            this.currentPaymentType = null;
            this._applyCart(result);
        } catch(error) {
            console.error('[ShoppingCartService] Failed to start put on tab:', error);
            error?.displayErrorToast?.();
            this._reloadSessionIfRefused(error);
            throw error;
        } finally {
            this.actionInFlight = null;
        }
    }

    public async confirmPayment(paymentData?: IShoppingCartServicePaymentData): Promise<IShoppingCartWrapper> {
        this._beginAction('confirmPayment');

        try {
            this._assertCartContext('confirmPayment');
            // The amount is fixed once startPayment ran, so a late click must not be booked any more.
            await this.cartActionInFlight;
            this._cancelPendingActions();

            const loadedCart = await this._ensureCartLoaded();
            const cartId = loadedCart.shoppingCart_ID;
            const serial = loadedCart.serial;
            console.log('[ShoppingCartService] Payment confirmed with type:', this.currentPaymentType);
            console.log('[ShoppingCartService] Payment data:', paymentData);
            const paidCart = await this.api.ShoppingCartV2Worker.paySegment(cartId, serial, {
                detailLevel:      this._viewMode(),
                paymentMethod_ID: this.currentPaymentType ?? undefined,
                ...paymentData
            });
            this._resetCartContext();
            return paidCart;
        } catch(error) {
            console.error('[ShoppingCartService] Failed to confirm payment:', error);
            error?.displayErrorToast?.();
            this._reloadSessionIfRefused(error);
            throw error;
        } finally {
            this.actionInFlight = null;
        }
    }

    public async goToCart(options: IShoppingCartGoToParams) {
        if (options.cartId) {
            this.cartId = options.cartId;
            this.router.navigate(['checkout', 'products', { cartID: options.cartId }]).catch(e => console.warn(e));
            return;
        }

        await this.createCart({
            ownerId:  options.userId,
            viewMode: this._viewMode()
        });

        this.router.navigate(['checkout', 'products', { cartID: this._cartId() }]).catch(e => console.warn(e));
    }

    // The session changed at another till of the drawer since the payment types were offered.
    private _reloadSessionIfRefused(error: unknown): void {
        if ((error as { status?: string })?.status === 'SHOPPINGCART:PAYMENT_TYPE_NOT_ALLOWED_WITHOUT_SESSION')
            void this.registerSessionService.reload();
    }

    private _resetCartContext(): void {
        this.currentPaymentType = null;
        this._products = [];
        this.cartId = null;
        this.rawCart.set(null);
    }

    public async cancelPaymentProcess(): Promise<void> {
        this._beginAction('cancelPaymentProcess');

        try {
            this._assertCartContext('cancelPaymentProcess');

            const loadedCart = await this._ensureCartLoaded();
            const cartId = loadedCart.shoppingCart_ID;
            const serial = loadedCart.serial;

            const result = await this.api.ShoppingCartV2Worker.cancelPaymentProcess(
                cartId,
                serial,
                { detailLevel: this._viewMode() }
            );

            await this._loadProductResources(result);
            this._applyCart(result);
            this.currentPaymentType = null;
            console.log('[ShoppingCartService] Payment process cancelled successfully');
        } catch(error) {
            console.error('[ShoppingCartService] Failed to cancel payment process:', error);
            error?.displayErrorToast?.();
            throw error;
        } finally {
            this.actionInFlight = null;
        }
    }

    private async _executeCartAction(
        productData: ShoppingCartProduct
    ): Promise<void> {
        this._assertCartContext('cart action');

        const cartId = this._cartId();
        const loadedCart = await this._ensureCartLoaded();
        if (this._cartId() !== cartId) {
            console.warn('[ShoppingCartService] Cart context changed while loading, skipping cart action');
            return;
        }

        let cart;
        if (productData.quantity > 0) {
            cart = await this.api.ShoppingCartV2Worker.addProduct(
                loadedCart.shoppingCart_ID,
                loadedCart.serial,
                productData,
                this._viewMode()
            );
        } else if (productData.quantity < 0) {
            cart = await this.api.ShoppingCartV2Worker.removeProduct(
                loadedCart.shoppingCart_ID,
                loadedCart.serial,
                productData.stack_ID,
                Math.abs(productData.quantity),
                this._viewMode()
            );
        } else {
            return;
        }

        await this._loadProductResources(cart);
        this._applyCart(cart);
    }

    private _transformCart(cart: IShoppingCartWrapper, readOnly: boolean): IShoppingCartServiceCart {
        const title = cart.checkout?.title || cart.flyout?.title || '';
        const immediateCancellationTil = this._immediateCancellationTil(cart);

        let products: Signal<IShoppingCartServiceProduct>[] = [];
        let transformedCart: IShoppingCartServiceCart = {
            ID:                            cart.shoppingCart_ID,
            title,
            productCount:                  0,
            totalValueProducts:            0,
            totalValueVats:                {},
            totalValue:                    0,
            immediateCancellationTil,
            snapshot:                      cart.snapshot,
            bankAccountsForUser:           cart.bankAccountsForUser,
            directDebitAccountInformation: cart.directDebitAccountInformation,
            balanceTotal:                  cart.balanceTotal,
            payment:                       cart.payment,
        };

        if (cart.checkout?.details) {
            const productMap = new Map<number, IShoppingCartDetailItem_FE[]>();

            cart.checkout.details.forEach(item => {
                const existing = productMap.get(item.stack_ID) || [];
                existing.push(item);
                productMap.set(item.stack_ID, existing);
            });

            products = Array.from(productMap.values()).map(items =>
                this._createProductSignal(items, readOnly)
            );

            transformedCart = {
                ...transformedCart,
                ...cart.checkout.calculation,
                productCount: products.length,
            };
        } else if (cart.flyout?.content) {
            const flyoutItems = Object.values(cart.flyout.content);

            products = flyoutItems.map(item =>
                this._createFlyoutProductSignal(item, readOnly)
            );

            const hasItemTotals = flyoutItems.some(item => item.valueTotal !== undefined);
            const itemsTotal = flyoutItems.reduce((sum, item) => sum + (item.valueTotal ?? 0), 0);
            const totalValueProducts = hasItemTotals ? itemsTotal : (cart.snapshot?.totalValue ?? 0);

            transformedCart = {
                ...transformedCart,
                productCount: products.length,
                totalValueProducts,
                totalValue:   totalValueProducts,
            };
        }

        if (readOnly)
            this._readOnlyProducts = products;
        else
            this._products = products;

        return transformedCart;
    }

    private _createProductSignal(items: IShoppingCartDetailItem_FE[], readOnly: boolean): Signal<IShoppingCartServiceProduct> {
        const firstItem = items[0];
        const totalQuantity = items.reduce((sum, item) => {
            return sum + item.quantity;
        }, 0);
        const stack_IDs = items.map(item => item.stack_ID).filter((id): id is number => id !== undefined);
        const productRevId = firstItem.product_rev_ID;
        const productResource = this._getOrCreateProductResource(productRevId);

        return computed(() => {
            const productData = productResource?.value() as IProductV2Edit | undefined;

            if (productData?.vat_ID && !this.vatRateSignals.has(productData.vat_ID))
                this._loadVatRate(productData.vat_ID);

            const vatRateSignal = productData?.vat_ID ? this.vatRateSignals.get(productData.vat_ID) : undefined;
            const vatRateValue = vatRateSignal?.() ?? null;
            const isLoading = !productData?.ID || (productData?.vat_ID && vatRateValue === null);
            const productCodes: string[] = productData?.productCodes || [];
            const vatRate = vatRateValue ?? 0;

            let bruttoPrice = 0;
            let nettoPrice = 0;
            const salesPrice = productData?.SalesPrice || 0;

            if (!isLoading) {
                if (productData?.isBrutto) {
                    bruttoPrice = salesPrice;
                    nettoPrice = vatRate > 0 ? Math.round(bruttoPrice / (1 + vatRate / 100)) : bruttoPrice;
                } else {
                    nettoPrice = salesPrice;
                    bruttoPrice = vatRate > 0 ? Math.round(nettoPrice * (1 + vatRate / 100)) : nettoPrice;
                }
            }

            const vatAmount = bruttoPrice - nettoPrice;

            return {
                ID:               productData?.ID || '',
                isLoading:        isLoading,
                productName:      productData?.title || '',
                productCode:      productCodes,
                stack_ID:         firstItem?.stack_ID,
                quantity:         totalQuantity,
                bruttoPrice:      bruttoPrice,
                nettoPrice:       nettoPrice,
                vatAmount:        vatAmount,
                vatRate:          vatRate,
                totalBruttoPrice: bruttoPrice * totalQuantity,
                totalNettoPrice:  nettoPrice * totalQuantity,
                totalVatAmount:   vatAmount * totalQuantity,
                cancellationType: 'none' as const,
                addedByDevice:    '',
                addedByUser:      '',
                at:               firstItem.at,
                action:           this._createDebouncedActionCallback(productRevId, stack_IDs, readOnly)
            };
        }) as Signal<IShoppingCartServiceProduct>;
    }

    private _createFlyoutProductSignal(item: IShoppingCartFlyoutItem_FE, readOnly: boolean): Signal<IShoppingCartServiceProduct> {
        const productRevId = item.product_rev_ID;
        const stack_IDs = [item.stack_ID];
        const productResource = this._getOrCreateProductResource(productRevId);

        return computed(() => {
            const productData = productResource?.value() as IProductV2Edit | undefined;

            if (productData?.vat_ID && !this.vatRateSignals.has(productData.vat_ID))
                this._loadVatRate(productData.vat_ID);

            const vatRateSignal = productData?.vat_ID ? this.vatRateSignals.get(productData.vat_ID) : undefined;
            const vatRateValue = vatRateSignal?.() ?? null;
            const isLoading = !productData?.ID || (productData?.vat_ID && vatRateValue === null);
            const productCodes: string[] = productData?.productCodes || [];
            const vatRate = vatRateValue ?? 0;

            let bruttoPrice = 0;
            let nettoPrice = 0;

            if (!isLoading) {
                if (item.valueTotal) {
                    bruttoPrice = Math.round(item.valueTotal / item.quantity);
                    nettoPrice = vatRate > 0 ? Math.round(bruttoPrice / (1 + vatRate / 100)) : bruttoPrice;
                } else {
                    const salesPrice = productData?.SalesPrice || 0;

                    if (productData?.isBrutto) {
                        bruttoPrice = salesPrice;
                        nettoPrice = vatRate > 0 ? Math.round(bruttoPrice / (1 + vatRate / 100)) : bruttoPrice;
                    } else {
                        nettoPrice = salesPrice;
                        bruttoPrice = vatRate > 0 ? Math.round(nettoPrice * (1 + vatRate / 100)) : nettoPrice;
                    }
                }
            }

            const vatAmount = bruttoPrice - nettoPrice;

            return {
                ID:               productData?.ID || '',
                isLoading:        isLoading,
                productName:      productData?.title || '',
                productCode:      productCodes,
                stack_ID:         item.stack_ID,
                quantity:         item.quantity,
                bruttoPrice:      bruttoPrice,
                nettoPrice:       nettoPrice,
                vatAmount:        vatAmount,
                vatRate:          vatRate,
                totalBruttoPrice: item.valueTotal ?? (bruttoPrice * item.quantity),
                totalNettoPrice:  nettoPrice * item.quantity,
                totalVatAmount:   vatAmount * item.quantity,
                cancellationType: 'none' as const,
                addedByDevice:    '',
                addedByUser:      '',
                at:               DateTime.now(),
                action:           this._createDebouncedActionCallback(productRevId, stack_IDs, readOnly)
            };
        }) as Signal<IShoppingCartServiceProduct>;
    }
}
